<template>
  <transition name="slide-right">
    <div v-if="visible" class="source-panel-overlay" @click.self="handleClose">
      <div class="source-panel">
        <div class="panel-header">
          <div class="header-left">
            <el-icon class="header-icon"><Document /></el-icon>
            <span class="header-title">溯源面板</span>
            <el-tag v-if="sources.length > 0" type="info" size="small" effect="plain">
              {{ sources.length }} 条引用
            </el-tag>
          </div>
          <div class="header-right">
            <el-tooltip content="导出溯源结果" placement="top">
              <el-button type="primary" size="small" text @click="handleExport">
                <el-icon><Download /></el-icon>
              </el-button>
            </el-tooltip>
            <el-button type="info" size="small" text @click="handleClose">
              <el-icon><Close /></el-icon>
            </el-button>
          </div>
        </div>

        <div class="panel-content">
          <div v-if="sources.length === 0" class="empty-state">
            <el-icon :size="48" color="#c0c4cc"><Document /></el-icon>
            <p class="empty-text">暂无引用来源</p>
            <p class="empty-hint">该回答可能未使用知识库内容</p>
          </div>

          <div v-else class="source-list">
            <div
              v-for="(source, index) in sources"
              :key="index"
              class="source-card"
              :class="{ 'is-graph': source.type === 'graph' }"
            >
              <div class="card-header">
                <div class="card-index">
                  <span class="index-badge">[{{ index + 1 }}]</span>
                </div>
                <div class="card-meta">
                  <span class="doc-name" :title="source.documentName">
                    {{ source.documentName || '未知文档' }}
                  </span>
                  <el-tag
                    v-if="source.type"
                    :type="source.type === 'vector' ? 'primary' : 'success'"
                    size="small"
                    effect="plain"
                    class="type-tag"
                  >
                    {{ source.type === 'vector' ? '向量检索' : '图谱检索' }}
                  </el-tag>
                </div>
              </div>

              <div v-if="source.score !== undefined && source.score !== null" class="card-score">
                <span class="score-label">相似度</span>
                <el-progress
                  :percentage="Math.round((source.score || 0) * 100)"
                  :stroke-width="8"
                  :color="getScoreColor(source.score)"
                  :format="scoreFormat"
                />
              </div>

              <div v-if="source.content" class="card-content">
                <div class="content-label">内容摘要</div>
                <div class="content-text" v-html="renderSourceContent(source)"></div>
                <el-button
                  v-if="source.content.length > 150"
                  type="primary"
                  size="small"
                  text
                  class="expand-btn"
                  @click="toggleExpand(index)"
                >
                  {{ expandedIndex === index ? '收起' : '展开更多' }}
                </el-button>
              </div>

              <div v-if="source.documentId" class="card-footer">
                <span class="doc-id">文档ID: {{ source.documentId }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="panel-footer">
          <div class="footer-info">
            <span class="citation-hint">提示: 回答中的 [1][2] 标注对应上方引用来源的序号</span>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { Document, Download, Close } from '@element-plus/icons-vue'
import { renderMarkdown } from '@/modules/huyu-framework/backend/utils/markdown'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  sources: {
    type: Array,
    default: () => []
  }
})

const emit = defineEmits(['update:visible'])

const expandedIndex = ref(null)

// 监听 visible 变化，重置展开状态
watch(() => props.visible, (newVal) => {
  if (newVal) {
    expandedIndex.value = null
  }
})

// 渲染来源内容（markdown）：未展开时按行截断保留完整行，避免截断破坏 markdown/图片语法
function renderSourceContent(source) {
  const content = source.content || ''
  if (expandedIndex.value !== null) {
    return renderMarkdown(content)
  }
  const maxLen = 150
  const lines = content.split('\n')
  const accLines = []
  let acc = 0
  for (const line of lines) {
    if (accLines.length > 0 && acc + line.length > maxLen) {
      break
    }
    accLines.push(line)
    acc += line.length
  }
  return renderMarkdown(accLines.join('\n'))
}

// 切换展开状态
function toggleExpand(index) {
  expandedIndex.value = expandedIndex.value === index ? null : index
}

// 获取相似度颜色
function getScoreColor(score) {
  if (score >= 0.8) return '#67c23a'
  if (score >= 0.6) return '#409eff'
  if (score >= 0.4) return '#e6a23c'
  return '#f56c6c'
}

// 格式化分数显示
function scoreFormat(percentage) {
  return (percentage / 100).toFixed(2)
}

// 关闭面板
function handleClose() {
  emit('update:visible', false)
}

// 导出溯源结果
function handleExport() {
  if (props.sources.length === 0) {
    ElMessage.warning('暂无溯源结果可导出')
    return
  }

  const exportData = {
    exportTime: new Date().toLocaleString('zh-CN'),
    totalSources: props.sources.length,
    sources: props.sources.map((source, index) => ({
      index: index + 1,
      documentName: source.documentName || '未知文档',
      type: source.type === 'vector' ? '向量检索' : '图谱检索',
      score: source.score !== undefined ? (source.score * 100).toFixed(2) + '%' : 'N/A',
      content: source.content || '',
      documentId: source.documentId || 'N/A'
    }))
  }

  const jsonStr = JSON.stringify(exportData, null, 2)
  const blob = new Blob([jsonStr], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `溯源结果_${new Date().toISOString().slice(0, 10)}.json`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)

  ElMessage.success('溯源结果已导出')
}
</script>

<style scoped>
/* 面板遮罩层 */
.source-panel-overlay {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 2000;
  display: flex;
  justify-content: flex-end;
}

/* 面板主体 */
.source-panel {
  width: 420px;
  max-width: 90vw;
  height: 100vh;
  background-color: #fff;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 面板头部 */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #ebeef5;
  background-color: #fafafa;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.header-icon {
  font-size: 18px;
  color: #409eff;
}

.header-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 面板内容 */
.panel-content {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

/* 空状态 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #c0c4cc;
}

.empty-text {
  font-size: 14px;
  color: #909399;
  margin-top: 12px;
}

.empty-hint {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 4px;
}

/* 来源列表 */
.source-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 来源卡片 */
.source-card {
  background-color: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px;
  transition: box-shadow 0.2s, border-color 0.2s;
}

.source-card:hover {
  border-color: #c0c4cc;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.source-card.is-graph {
  border-left: 3px solid #67c23a;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}

.card-index {
  flex-shrink: 0;
}

.index-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 24px;
  padding: 0 6px;
  background-color: #409eff;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
}

.card-meta {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.doc-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.type-tag {
  flex-shrink: 0;
}

/* 相似度 */
.card-score {
  margin-bottom: 12px;
}

.score-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
  display: block;
}

/* 内容摘要 */
.card-content {
  margin-bottom: 12px;
}

.content-label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 6px;
}

.content-text {
  font-size: 13px;
  color: #606266;
  line-height: 1.6;
  word-break: break-word;
  background-color: #fff;
  padding: 10px 12px;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.content-text :deep(p) {
  margin: 0 0 6px;
}

.content-text :deep(p:last-child) {
  margin-bottom: 0;
}

.content-text :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 4px;
  display: block;
  margin: 6px 0;
}

.expand-btn {
  margin-top: 8px;
  font-size: 12px;
}

/* 卡片底部 */
.card-footer {
  padding-top: 10px;
  border-top: 1px dashed #ebeef5;
}

.doc-id {
  font-size: 12px;
  color: #c0c4cc;
}

/* 面板底部 */
.panel-footer {
  padding: 12px 20px;
  border-top: 1px solid #ebeef5;
  background-color: #fafafa;
  flex-shrink: 0;
}

.footer-info {
  display: flex;
  align-items: center;
  justify-content: center;
}

.citation-hint {
  font-size: 12px;
  color: #909399;
  text-align: center;
}

/* 滑入动画 */
.slide-right-enter-active,
.slide-right-leave-active {
  transition: opacity 0.3s ease;
}

.slide-right-enter-active .source-panel,
.slide-right-leave-active .source-panel {
  transition: transform 0.3s ease;
}

.slide-right-enter-from,
.slide-right-leave-to {
  opacity: 0;
}

.slide-right-enter-from .source-panel,
.slide-right-leave-to .source-panel {
  transform: translateX(100%);
}
</style>
